import React from 'react';
import { Target, Eye, Sparkles, CheckCircle2, ArrowRight, Globe } from 'lucide-react';
import { companyInfo } from '../data/companyInfo';
import { Logo } from '../components/ui/Logo';

interface AboutPageProps {
  onNavigate: (path: string) => void;
  onOpenConsultation: () => void;
}

export const AboutPage: React.FC<AboutPageProps> = ({ onNavigate, onOpenConsultation }) => {
  return (
    <div className="bg-[#071426] text-[#D5DFEC] py-12 space-y-16">
      
      {/* Page Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
              <span>ABOUT EBON MEDIA SOLUTIONS</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-display tracking-tight leading-tight">
              Engineering Sustainable Digital Growth for Modern Enterprises
            </h1>
            <p className="text-base sm:text-lg text-[#D5DFEC] leading-relaxed">
              EBON Media Solutions was founded on a simple conviction: technology should create clear business progress, streamline operations, and unlock lasting competitive advantages.
            </p>
            <div className="pt-2 flex flex-wrap items-center gap-4 text-xs text-[#8FA0B8]">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#159DE8]" />
                <span className="text-white font-medium">Bespoke Software & Web Architecture</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#FF851B]" />
                <span className="text-white font-medium">Hands-On Client Collaboration</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden border border-[#1b2f4a] bg-[#050A13] shadow-2xl group">
              <img
                src="/assets/about-hero-planning.jpg"
                alt="EBON Media Solutions - Strategic Technology Planning & Architecture Session"
                className="w-full h-[320px] sm:h-[380px] object-cover group-hover:scale-[1.02] transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071426] via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 bg-[#050A13]/90 backdrop-blur-md px-4 py-3 rounded-xl border border-[#1b2f4a] flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-white block">Collaborative Engineering</span>
                  <span className="text-[11px] text-[#8FA0B8]">Every project begins with transparent architectural mapping</span>
                </div>
                <span className="text-[10px] font-mono px-2.5 py-1 rounded bg-[#071426] text-[#159DE8] border border-[#1b2f4a] font-medium shrink-0">
                  Sprint Zero
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision — Premium Split Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-3">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-8 bg-[#FF851B]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B]">OUR PURPOSE</span>
            <span className="h-px w-8 bg-[#FF851B]" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display">
            Why We Exist & Where We're Going
          </h2>
          <p className="text-sm text-[#8FA0B8]">The convictions that drive every solution we engineer and every partnership we build.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Mission */}
          <div className="relative p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-[#0B2342] to-[#071426] border border-[#159DE8]/30 overflow-hidden shadow-2xl group hover:border-[#159DE8]/60 transition-all duration-300">
            {/* Ambient glow */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#159DE8]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#159DE8]/20 border border-[#159DE8]/40 flex items-center justify-center shrink-0">
                  <Target size={28} className="text-[#159DE8]" />
                </div>
                <div>
                  <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#159DE8]">Our Mission</span>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-white font-display leading-tight">What We Do Every Day</h3>
                </div>
              </div>
              <div className="h-px bg-gradient-to-r from-[#159DE8]/40 to-transparent" />
              <p className="text-base text-[#D5DFEC] leading-relaxed">
                To empower startups, mid-market enterprises, and organizations with intelligent software architectures, robust mobile apps, and data-driven marketing strategies that turn operational ambition into commercial reality.
              </p>
              <div className="space-y-3 pt-2">
                {[
                  'Build technology around real business goals',
                  'Deliver measurable ROI on every engagement',
                  'Make enterprise-grade software accessible to all businesses',
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-[#D5DFEC]">
                    <CheckCircle2 size={16} className="text-[#159DE8] shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Vision */}
          <div className="relative p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-[#1a0b2e] to-[#071426] border border-[#FF851B]/30 overflow-hidden shadow-2xl group hover:border-[#FF851B]/60 transition-all duration-300">
            {/* Ambient glow */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#FF851B]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#FF851B]/20 border border-[#FF851B]/40 flex items-center justify-center shrink-0">
                  <Eye size={28} className="text-[#FF851B]" />
                </div>
                <div>
                  <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#FF851B]">Our Vision</span>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-white font-display leading-tight">Where We're Headed</h3>
                </div>
              </div>
              <div className="h-px bg-gradient-to-r from-[#FF851B]/40 to-transparent" />
              <p className="text-base text-[#D5DFEC] leading-relaxed">
                To be an internationally recognized digital growth and engineering partner known for technical integrity, human-centric design, and software systems that scale smoothly and deliver value for decades.
              </p>
              <div className="space-y-3 pt-2">
                {[
                  'Recognized globally for technical excellence',
                  'Create enduring value through scalable software systems',
                  'Become the most trusted partner for digital transformation',
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-[#D5DFEC]">
                    <CheckCircle2 size={16} className="text-[#FF851B] shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership & Engineering Standards Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-[#050A13] border border-[#1b2f4a] shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-5 relative">
              <div className="rounded-2xl overflow-hidden border border-[#1b2f4a] bg-[#071426] shadow-xl group">
                <img 
                  src="/assets/about-team-discussion.jpg" 
                  alt="EBON Media Solutions - Senior Engineers & Strategists in Active Discussion" 
                  className="w-full h-[320px] sm:h-[380px] object-cover group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="p-4 bg-[#071426] border-t border-[#1b2f4a] flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-white">Engineering Leadership</h3>
                    <p className="text-xs text-[#159DE8] font-medium">Focused Technical Discussion</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[#8FA0B8]">
                    <div className="w-2 h-2 rounded-full bg-[#159DE8] animate-pulse" />
                    <span>Active Sprints</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#159DE8] uppercase tracking-wider">
                  <Sparkles size={14} />
                  <span>Technical Excellence</span>
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
                Guided by Strategic Vision & Engineering Integrity
              </h2>

              <p className="text-sm sm:text-base text-[#D5DFEC] leading-relaxed">
                At EBON Media Solutions, our leadership fosters a culture of technical rigor, transparent execution, and enduring client partnerships. We build modern digital products that ensure long-term website performance, security, and market reach across the US, UK, and Canada.
              </p>

              <blockquote className="p-4 rounded-xl bg-[#0B2342]/40 border-l-4 border-[#FF851B] text-xs sm:text-sm text-white italic">
                "We don't simply launch a website or application and disappear. We maintain close, long-term technical partnerships with our clients—taking care of their digital assets continuously so they consistently capture market opportunities."
              </blockquote>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-xs">
                <div className="flex items-center gap-2 text-[#D5DFEC]">
                  <CheckCircle2 size={16} className="text-[#159DE8] shrink-0" />
                  <span>Ethical, transparent client partnerships</span>
                </div>
                <div className="flex items-center gap-2 text-[#D5DFEC]">
                  <CheckCircle2 size={16} className="text-[#159DE8] shrink-0" />
                  <span>Rigorous code quality & architecture standards</span>
                </div>
                <div className="flex items-center gap-2 text-[#D5DFEC]">
                  <CheckCircle2 size={16} className="text-[#159DE8] shrink-0" />
                  <span>Client owns 100% of IP & source code</span>
                </div>
                <div className="flex items-center gap-2 text-[#D5DFEC]">
                  <CheckCircle2 size={16} className="text-[#159DE8] shrink-0" />
                  <span>Continuous post-launch SLA support</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
            The Principles That Drive Our Work
          </h2>
          <p className="text-xs sm:text-sm text-[#8FA0B8]">
            Built into every line of code, design mock, and strategic sprint.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {companyInfo.coreValues.map((v, idx) => (
            <div key={idx} className="p-6 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] space-y-2.5">
              <span className="text-xs font-mono font-bold text-[#FF851B]">0{idx + 1}</span>
              <h3 className="text-base font-bold text-white">{v.title}</h3>
              <p className="text-xs text-[#8FA0B8] leading-relaxed">{v.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-[#0B2342] to-[#071426] border border-[#1b2f4a] flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-white font-display">Ready to discuss your digital transformation?</h2>
            <p className="text-sm text-[#D5DFEC]">Book a direct session with our senior engineers and leadership team.</p>
          </div>
          <button
            onClick={onOpenConsultation}
            className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-sm font-bold shadow-lg hover:shadow-glow-orange transition-all shrink-0 flex items-center gap-2"
          >
            <span>Book a Consultation</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

    </div>
  );
};
